import { FormBlock } from '@qbe/ads-blocks-forms';
import { FormGroupLabel, HighlightContainer, Typography } from '@qbe/ads-core';
import React from 'react';
import { useSelector } from 'react-redux';

import CurrencyField from '~/components-ds/CurrencyField';
import { CURRENCY_TEXTFIELD_LIMIT } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useCoverDetails } from '~/hooks';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { updateGrossIncome } from '~/slices/coverDetails';
import { qalFormStartAction } from '~/thunks/analytics';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

interface GrossIncomeProps {}

const GrossIncome: React.FC<GrossIncomeProps> = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBusinessInterruption',
  });

  const dispatch = useAppDispatch();
  const { grossIncome } = useCoverDetails();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const grossIncomeTitle = isPropertyOwner
    ? getTextContent('fields.grossIncome.landlordTitle')
    : getTextContent('fields.grossIncome.businessOwnerTitle');

  return (
    <>
      <FormGroupLabel
        title={grossIncomeTitle}
        alignCenter
        titleSuffix={
          <ContextualHelpWithButton tracking={`help: ${grossIncomeTitle}`}>
            <Typography>
              {isPropertyOwner
                ? getJSXContent('contextualhelpers.grossIncomeLandlord.description')
                : getJSXContent('contextualhelpers.grossIncomeBusinessOwner.description')}
            </Typography>
          </ContextualHelpWithButton>
        }
      />

      <FormBlock preset="text_questionDesc">
        <Typography textAlign="center">
          {isPropertyOwner
            ? getTextContent('fields.grossIncome.landlordDescription')
            : getTextContent('fields.grossIncome.businessOwnerDescription')}
        </Typography>
      </FormBlock>

      <FormBlock preset="highlight_container">
        <HighlightContainer>
          {getJSXContent('content.grossIncomePeriods.description')}
        </HighlightContainer>
      </FormBlock>

      <FormBlock preset="input" maxWidth="100%">
        <CurrencyField
          id="grossIncome"
          name="gross income"
          label={getTextContent('fields.grossIncome.label')}
          data-track="gross income amount"
          data-track-value="mask"
          helperText={
            isPropertyOwner
              ? getTextContent('fields.grossIncome.landlordHelperText')
              : getTextContent('fields.grossIncome.businessOwnerHelperText')
          }
          errorText={
            isPropertyOwner
              ? getTextContent('fields.grossIncome.landlordInvalid')
              : getTextContent('fields.grossIncome.businessOwnerInvalid')
          }
          defaultValue={grossIncome?.toString()}
          fieldValidation={{
            validations: {
              numericOnly: true,
              required: true,
              maxLength: CURRENCY_TEXTFIELD_LIMIT,
            },
            action: updateGrossIncome,
          }}
          onChange={handleFormStart}
          required
        />
      </FormBlock>
    </>
  );
};

export default GrossIncome;
